import { priorities, statuses, types } from "@/components/task/utils";
import { DataTable } from "@/components/ui/datatable";
import { DataTableColumnHeader } from "@/components/ui/datatable/column-header";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "@/components/ui/select";
import { DataService } from "@/services/data";
import { TaskType } from "@/types/user";
import { ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useRevalidator } from "react-router-dom";
import { toast } from "sonner";
import { DialogInner } from "./task-dialog-content";

export const TaskDataTable = ({ tasks, is_manager }: { tasks: TaskType[]; is_manager: boolean }) => {
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<TaskType | null>(null);

    useEffect(() => {
        if (selectedRow) {
            setIsDialogOpen(true);
        } else {
            setIsDialogOpen(false);
        }
    }, [selectedRow]);

    let columns: ColumnDef<TaskType>[] = [
        {
            id: "title",
            accessorFn: row => row.title,
            header: ({ column }) => <DataTableColumnHeader column={column} title="Titre" />,
            cell: ({ row }) => (
                <p className="flex flex-row justify-start whitespace-nowrap">
                    <span>{row.original.title}</span>
                    {row.original.reference !== null && row.original.reference_type === "apprenticeship:registration" && (
                        <>
                            <span className="mx-1">-</span>
                            <span
                                className="flex cursor-pointer flex-row items-center space-x-1 font-medium text-card-foreground hover:underline"
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (event.metaKey || event.ctrlKey) {
                                        const win = window.open(`/alternance/candidats/${row?.original?.reference?.id}/dossier`, "_blank");
                                        win?.focus();
                                    } else {
                                        navigate(`/alternance/candidats/${row?.original?.reference?.id}/dossier`);
                                    }
                                }}
                            >
                                <span>
                                    {row?.original?.reference.first_name} {row?.original?.reference.last_name}
                                </span>
                                <ExternalLink size={16} />
                            </span>
                        </>
                    )}
                </p>
            )
        },
        {
            id: "description",
            accessorFn: row => row.description,
            header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
            cell: ({ row }) => (
                <p className="flex flex-row">
                    <span>{row.original.description}</span>
                </p>
            )
        },
        {
            id: "type",
            accessorFn: row => row.type,
            header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
            cell: ({ row }) => {
                const type = types.find(type => type.value === row.getValue("type"));
                if (!type) {
                    return null;
                }
                return (
                    <div className="flex flex-row items-center space-x-1">
                        <div>{type.icon}</div>
                        <div>{type.label}</div>
                    </div>
                );
            }
        },
        {
            id: "due_date",
            accessorFn: row => (row.due_date ? new Date(row.due_date).getTime() : null),
            header: ({ column }) => <DataTableColumnHeader column={column} title="Due date" />,
            cell: ({ row }) => (row.original.due_date ? new Date(row.original.due_date).toLocaleDateString() : "-")
        },
        {
            accessorKey: "status",
            header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
            cell: ({ row }) => {
                const status = statuses.find(status => status.value === row.getValue("status"));
                if (!status) {
                    return null;
                }
                return (
                    <Select
                        disabled={row.original.status_editable === false}
                        onValueChange={async value => {
                            await DataService.client
                                .url(`/users/tasks/${row.original.id}/`)
                                .patch({
                                    status: value
                                })
                                .json()
                                .then(() => {
                                    revalidator.revalidate();
                                })
                                .catch(() => {
                                    toast.error("Une erreur est survenue");
                                });
                        }}
                    >
                        <SelectTrigger className="h-7 px-1 py-0">
                            <div className="flex flex-row items-center space-x-1">
                                <div>{status.icon}</div>
                                <div>{status.label}</div>
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                {statuses.map((s, index) => {
                                    return (
                                        <SelectItem value={s.value} key={index}>
                                            <div className="flex flex-row items-center space-x-1">
                                                <div>{s.icon}</div>
                                                <div>{s.label}</div>
                                            </div>
                                        </SelectItem>
                                    );
                                })}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                );
            },
            filterFn: (row, id, value) => {
                return value.includes(row.getValue(id));
            }
        },
        {
            accessorKey: "priority",
            header: ({ column }) => <DataTableColumnHeader column={column} title="Priority" />,
            cell: ({ row }) => {
                row.original.id;
                const priority = priorities.find(priority => priority.value === row.getValue("priority"));
                if (!priority) {
                    return null;
                }

                return (
                    <Select
                        disabled={row.original.priority_editable === false}
                        onValueChange={async value => {
                            await DataService.client
                                .url(`/users/tasks/${row.original.id}/`)
                                .patch({
                                    priority: value
                                })
                                .json()
                                .then(() => {
                                    revalidator.revalidate();
                                })
                                .catch(() => {
                                    toast.error("Une erreur est survenue");
                                });
                        }}
                    >
                        <SelectTrigger className="h-7 px-1 py-0">
                            <div className="flex flex-row items-center space-x-1">
                                <div>{priority.icon}</div>
                                <div>{priority.label}</div>
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                {priorities.map((p, index) => {
                                    return (
                                        <SelectItem value={p.value} key={index}>
                                            <div className="flex flex-row items-center space-x-1">
                                                <div>{p.icon}</div>
                                                <div>{p.label}</div>
                                            </div>
                                        </SelectItem>
                                    );
                                })}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                );
            },
            filterFn: (row, id, value) => {
                return value.includes(row.getValue(id));
            }
        }
    ];

    if (is_manager) {
        columns = [
            {
                id: "assigned_to",
                accessorFn: row => row.type,
                header: ({ column }) => <DataTableColumnHeader column={column} title="Owner" />,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-row items-center space-x-1">
                            <div>
                                {row.original.assigned_to?.first_name} {row.original.assigned_to?.last_name}
                            </div>
                        </div>
                    );
                }
            },
            ...columns
        ];
    }

    return (
        <>
            <DataTable
                onRowClick={row => {
                    setSelectedRow(row.original);
                }}
                columns={columns}
                data={tasks}
                filters={[
                    {
                        type: "input",
                        key: "search",
                        placeholder: "Search a task..."
                    },
                    {
                        type: "date-range",
                        key: "due_date"
                    },
                    {
                        type: "select",
                        key: "status",
                        placeholder: "Status",
                        options: [{ value: "all", label: "All" }].concat(statuses)
                    },
                    {
                        type: "select",
                        key: "priority",
                        placeholder: "Priority",
                        options: [{ value: "all", label: "All" }].concat(priorities)
                    },
                    {
                        type: "select",
                        key: "type",
                        placeholder: "Type",
                        options: [{ value: "all", label: "All" }].concat(types)
                    }
                ]}
            />
            <Dialog
                open={isDialogOpen}
                onOpenChange={open => {
                    if (!open) {
                        setSelectedRow(null);
                    }
                }}
            >
                <DialogTrigger asChild>
                    <DialogContent className="flex w-auto min-w-96 flex-1 flex-col">
                        {selectedRow && selectedRow.reference && selectedRow.reference_type == "apprenticeship:registration" && (
                            <DialogInner task={selectedRow} isDialogOpen={isDialogOpen} setIsDialogOpen={setIsDialogOpen} />
                        )}
                    </DialogContent>
                </DialogTrigger>
            </Dialog>
        </>
    );
};
